export * from "ce/sagas/ApplicationSagas";
import {
  publishApplicationSaga,
  updateApplicationLayoutSaga,
  updateApplicationSaga,
  changeAppViewAccessSaga,
  fetchAppAndPagesSaga,
  forkApplicationSaga,
  createApplicationSaga,
  setDefaultApplicationPageSaga,
  deleteApplicationSaga,
  importApplicationSaga,
  fetchReleases,
  initDatasourceConnectionDuringImport,
  showReconnectDatasourcesModalSaga,
  fetchUnconfiguredDatasourceList,
  uploadNavigationLogoSaga,
  deleteNavigationLogoSaga,
  fetchAllApplicationsOfWorkspaceSaga,
} from "ce/sagas/ApplicationSagas";
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import type {
  ReduxAction,
  ReduxActionWithPromise,
} from "actions/ReduxActionTypes";
import type {
  ChangeAppUserRoleRequest,
  DeleteApplicationUserRequest,
  FetchAllAppRolesRequest,
  FetchAllAppRolesResponse,
  FetchAllAppUsersRequest,
  FetchAllAppUsersResponse,
  InviteUserToAppRequest,
} from "ee/api/ApplicationApi";
import ApplicationApi from "ee/api/ApplicationApi";
import {
  callAPI,
  getResponseErrorMessage,
  validateResponse,
} from "sagas/ErrorSagas";
import type { ApiResponse } from "api/ApiResponses";
import { INVITE_USERS_TO_WORKSPACE_FORM } from "ee/constants/forms";
import { reset } from "redux-form";
import type { User } from "constants/userConstants";
import { getCurrentUser } from "selectors/usersSelectors";
import history from "utils/history";
import { APPLICATIONS_URL } from "constants/routes";
import { toast } from "@appsmith/ads";
import { publishApplication } from "ce/actions/applicationActions";
import { getIsAnyActionSchemaDirty } from "ee/selectors/actionSchemaSelectors";
import {
  hideWarningPublishAnvilApplication,
  saveSchemaPostWarningSuccess,
  showWarningPublishAnvilApplication,
} from "ee/actions/generateSchemaActions";
import { SchemaAPI } from "ee/api/SchemaAPI";
import { getAgentChatQueries } from "ee/selectors/aiAgentSelectors";
import type { ActionData } from "ee/reducers/entityReducers/actionsReducer";

export function* fetchAllAppUsersSaga(
  action: ReduxAction<FetchAllAppUsersRequest>,
) {
  try {
    const request: FetchAllAppUsersRequest = action.payload;
    const response: FetchAllAppUsersResponse = yield call(
      ApplicationApi.fetchApplicationUsers,
      request,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      // TODO: Fix this the next time the file is edited
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const users = response.data.map((user: any) => ({
        ...user,
        isDeleting: false,
        isChangingRole: false,
      }));

      yield put({
        type: ReduxActionTypes.FETCH_ALL_APP_USERS_SUCCESS,
        payload: users,
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_ALL_APP_USERS_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* fetchAllAppRolesSaga(
  action: ReduxAction<FetchAllAppRolesRequest>,
) {
  try {
    const request: FetchAllAppRolesRequest = action.payload;
    const response: FetchAllAppRolesResponse = yield call(
      ApplicationApi.fetchApplicationRoles,
      request,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_ALL_APP_ROLES_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_ALL_APP_ROLES_ERROR,
    });
  }
}

export function* fetchAppDefaultRolesSaga() {
  try {
    const response: FetchAllAppRolesResponse = yield call(
      ApplicationApi.fetchDefaultApplicationRoles,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.FETCH_APP_DEFAULT_ROLES_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.FETCH_APP_DEFAULT_ROLES_ERROR,
    });
  }
}

export function* inviteUsersToApplicationSaga(
  action: ReduxActionWithPromise<{
    data: InviteUserToAppRequest;
  }>,
) {
  const { data, reject, resolve } = action.payload;

  try {
    const response: ApiResponse = yield callAPI(
      ApplicationApi.inviteUsersToApplication,
      {
        usernames: data.usernames,
        groups: data.groups,
        applicationId: data.applicationId,
        roleType: data.roleType,
        recaptchaToken: data.recaptchaToken,
      },
    );
    const isValidResponse: boolean = yield validateResponse(response, false);

    if (!isValidResponse) {
      let errorMessage = `${data.usernames}:  `;

      errorMessage += getResponseErrorMessage(response);
      yield call(reject, { _error: errorMessage });
    }

    yield put({
      type: ReduxActionTypes.FETCH_ALL_APP_USERS_INIT,
      payload: {
        applicationId: data.applicationId,
      },
    });
    yield call(resolve);
    yield put(reset(INVITE_USERS_TO_WORKSPACE_FORM));
  } catch (error) {
    yield call(reject, { _error: (error as Error).message });
  }
}

export function* deleteApplicationUserSaga(
  action: ReduxAction<DeleteApplicationUserRequest>,
) {
  try {
    const request: DeleteApplicationUserRequest = action.payload;
    // TODO: Fix this the next time the file is edited
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: ApiResponse & { data: any } = yield call(
      ApplicationApi.deleteApplicationUser,
      request,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      const currentUser: User | undefined = yield select(getCurrentUser);

      if (currentUser?.username == action.payload.username) {
        history.replace(APPLICATIONS_URL);
      } else {
        yield put({
          type: ReduxActionTypes.DELETE_APPLICATION_USER_SUCCESS,
          payload: {
            username: action.payload.username,
            userGroupId: action.payload.userGroupId,
            applicationId: action.payload.applicationId,
          },
        });
      }

      toast.show(
        `${
          response.data?.username || response.data?.name
        } has been removed successfully`,
        {
          kind: "success",
        },
      );
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.DELETE_APPLICATION_USER_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* changeApplicationUserRoleSaga(
  action: ReduxAction<ChangeAppUserRoleRequest>,
) {
  try {
    const request: ChangeAppUserRoleRequest = action.payload;
    const response: ApiResponse = yield call(
      ApplicationApi.changeApplicationUserRole,
      request,
    );
    const isValidResponse: boolean = yield validateResponse(response);

    if (isValidResponse) {
      yield put({
        type: ReduxActionTypes.CHANGE_APPLICATION_USER_ROLE_SUCCESS,
        payload: response.data,
      });
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.CHANGE_APPLICATION_USER_ROLE_ERROR,
      payload: {
        error,
      },
    });
  }
}

export function* publishAnvilApplicationSaga(
  action: ReduxAction<{ applicationId: string }>,
) {
  try {
    const { applicationId } = action.payload;
    const agentChatQueries: ActionData[] = yield select(getAgentChatQueries);
    let isAnyActionSchemaDirty: boolean = false;

    for (const query of agentChatQueries) {
      isAnyActionSchemaDirty = yield select(
        getIsAnyActionSchemaDirty(query.config.id),
      );
    }

    if (!isAnyActionSchemaDirty) {
      yield put(publishApplication(applicationId));
    } else {
      yield put(showWarningPublishAnvilApplication());

      const {
        ignoreWarningPublishAnvilApplication,
        saveSchemaPostWarningInit,
      } = yield race({
        saveSchemaPostWarningInit: take(
          ReduxActionTypes.SAVE_SCHEMA_POST_WARNING_INIT,
        ),
        hideWarningPublishAnvilApplication: take(
          ReduxActionTypes.HIDE_WARNING_PUBLISH_ANVIL_APPLICATION,
        ),
        ignoreWarningPublishAnvilApplication: take(
          ReduxActionTypes.IGNORE_WARNING_PUBLISH_ANVIL_APPLICATION,
        ),
      });

      if (ignoreWarningPublishAnvilApplication) {
        yield put(hideWarningPublishAnvilApplication());
        yield put(publishApplication(applicationId));
      } else if (saveSchemaPostWarningInit) {
        if (agentChatQueries.length) {
          yield all(
            agentChatQueries.map((query) =>
              call(SchemaAPI.generateAIAgentSchema, query.config.id),
            ),
          );
        }

        yield put(saveSchemaPostWarningSuccess());
        yield put(hideWarningPublishAnvilApplication());

        yield put(publishApplication(applicationId));
      }
    }
  } catch (error) {
    yield put({
      type: ReduxActionErrorTypes.PUBLISH_ANVIL_APPLICATION_ERROR,
      payload: {
        error,
      },
    });
  }
}

export default function* applicationSagas() {
  yield all([
    takeLatest(
      ReduxActionTypes.PUBLISH_APPLICATION_INIT,
      publishApplicationSaga,
    ),
    takeLatest(ReduxActionTypes.UPDATE_APP_LAYOUT, updateApplicationLayoutSaga),
    takeLatest(ReduxActionTypes.UPDATE_APPLICATION, updateApplicationSaga),
    takeLatest(
      ReduxActionTypes.CHANGE_APPVIEW_ACCESS_INIT,
      changeAppViewAccessSaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_ALL_APPLICATIONS_OF_WORKSPACE_INIT,
      fetchAllApplicationsOfWorkspaceSaga,
    ),
    takeLatest(ReduxActionTypes.FETCH_APPLICATION_INIT, fetchAppAndPagesSaga),
    takeLatest(ReduxActionTypes.FORK_APPLICATION_INIT, forkApplicationSaga),
    takeLatest(ReduxActionTypes.CREATE_APPLICATION_INIT, createApplicationSaga),
    takeLatest(
      ReduxActionTypes.SET_DEFAULT_APPLICATION_PAGE_INIT,
      setDefaultApplicationPageSaga,
    ),
    takeLatest(ReduxActionTypes.DELETE_APPLICATION_INIT, deleteApplicationSaga),
    takeLatest(ReduxActionTypes.IMPORT_APPLICATION_INIT, importApplicationSaga),
    takeLatest(
      ReduxActionTypes.UPLOAD_NAVIGATION_LOGO_INIT,
      uploadNavigationLogoSaga,
    ),
    takeLatest(
      ReduxActionTypes.DELETE_NAVIGATION_LOGO_INIT,
      deleteNavigationLogoSaga,
    ),
    takeLatest(ReduxActionTypes.FETCH_RELEASES, fetchReleases),
    takeLatest(
      ReduxActionTypes.INIT_DATASOURCE_CONNECTION_DURING_IMPORT_REQUEST,
      initDatasourceConnectionDuringImport,
    ),
    takeLatest(
      ReduxActionTypes.SHOW_RECONNECT_DATASOURCE_MODAL,
      showReconnectDatasourcesModalSaga,
    ),
    takeLatest(
      ReduxActionTypes.FETCH_UNCONFIGURED_DATASOURCE_LIST,
      fetchUnconfiguredDatasourceList,
    ),
    takeLatest(ReduxActionTypes.FETCH_ALL_APP_USERS_INIT, fetchAllAppUsersSaga),
    takeLatest(ReduxActionTypes.FETCH_ALL_APP_ROLES_INIT, fetchAllAppRolesSaga),
    takeLatest(
      ReduxActionTypes.FETCH_APP_DEFAULT_ROLES_INIT,
      fetchAppDefaultRolesSaga,
    ),
    takeLatest(
      ReduxActionTypes.INVITE_USERS_TO_APPLICATION_INIT,
      inviteUsersToApplicationSaga,
    ),
    takeLatest(
      ReduxActionTypes.DELETE_APPLICATION_USER_INIT,
      deleteApplicationUserSaga,
    ),
    takeLatest(
      ReduxActionTypes.CHANGE_APPLICATION_USER_ROLE_INIT,
      changeApplicationUserRoleSaga,
    ),
    takeLatest(
      ReduxActionTypes.PUBLISH_ANVIL_APPLICATION_INIT,
      publishAnvilApplicationSaga,
    ),
  ]);
}
